import React, { useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Copy, RotateCcw, X } from "lucide-react";
import TransformControls from "./panels/TransformControls";
import StyleControls from "./panels/StyleControls";
import TextControls from "./panels/TextControls";
import ImageControls from "./panels/ImageControls";
import CornerRadiusControls from "./panels/CornerRadiusControls";
import AnimationControls from "./panels/AnimationControls";
import ExportPanel from "./panels/ExportPanel";

function PanelFrame({ title, isCompact, onClose, headerActions, children }) {
  return (
    <aside className="floating-panel w-full h-full flex flex-col overflow-hidden">
      <div className="h-12 border-b border-line flex items-center justify-between px-4 shrink-0">
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-ink4">{title}</span>
        <div className="flex items-center gap-1">
          {headerActions}
          {isCompact && (
            <button onClick={onClose} data-testid="right-panel-close-btn" className="h-7 w-7 flex items-center justify-center text-ink4 hover:text-ink hover:bg-hover transition-colors" title="Close">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto scroll-thin p-4 space-y-5">{children}</div>
    </aside>
  );
}

export default function RightPanel({
  element,
  selectedElements = [],
  allElements = [],
  background = "transparent",
  onUpdate,
  onDelete,
  onDuplicate,
  onReplay,
  onReplaceImage,
  isCompact,
  onClose,
}) {
  const id = element?.id;
  const attrs = useMemo(() => element?.attrs || {}, [element]);
  const effects = useMemo(() => element?.effects || {}, [element]);

  // setAttr accepts either (key, value) for a single field, or a patch object
  // for merging multiple fields at once. The patch form avoids the stale-closure
  // trap that occurs when two sequential setAttr calls reference the same attrs
  // snapshot (the second would clobber the first's change).
  const setAttr = useCallback(
    (keyOrPatch, value) => {
      const patch = typeof keyOrPatch === "string" ? { [keyOrPatch]: value } : keyOrPatch;
      onUpdate(id, { attrs: { ...attrs, ...patch } });
    },
    [id, attrs, onUpdate]
  );

  const setEffect = useCallback(
    (group, patch) => onUpdate(id, { effects: { ...effects, [group]: { ...(effects[group] || {}), ...patch } } }),
    [id, effects, onUpdate]
  );

  const setAnim = useCallback(
    (patch) => {
      if (!patch) return onUpdate(id, { animation: null });
      // Use a functional patch so we merge against the freshest element state
      // in the store, not the `element` prop snapshot captured in this closure.
      // This prevents stale-closure bugs when several setAnim calls are issued
      // in the same tick (e.g. onTypeChange immediately followed by param
      // edits) — the second call would otherwise clobber the first.
      onUpdate(id, (el) => ({ animation: { ...(el?.animation || {}), ...patch } }));
    },
    [id, onUpdate]
  );

  // Export target: the current selection, or the whole canvas when nothing is
  // selected. Scope label mirrors that.
  const exportTargets = selectedElements.length ? selectedElements : allElements;
  const exportScope =
    selectedElements.length === 1
      ? `当前图形 · 1 selected (${selectedElements[0].type})`
      : selectedElements.length > 1
      ? `已选 ${selectedElements.length} 个图形 · ${selectedElements.length} selected`
      : `整个画布 · whole canvas (${allElements.length})`;

  const exportSection = (
    <ExportPanel targets={exportTargets} background={background} scope={exportScope} />
  );

  // No single element selected (empty OR multi-selection): show a light panel
  // with selection info + the export tools at the bottom.
  if (!element) {
    return (
      <PanelFrame title="Inspector" isCompact={isCompact} onClose={onClose}>
        <div className="text-xs text-ink4 font-mono leading-relaxed">
          {selectedElements.length > 1 ? (
            <span data-testid="multi-selection-hint">已选 {selectedElements.length} 个图形 · 可框选组合导出</span>
          ) : (
            <>
              NO SELECTION.
              <br />
              <br />
              Drag a shape from the left, or click an existing element on the canvas.
            </>
          )}
        </div>
        {exportSection}
      </PanelFrame>
    );
  }

  return (
    <PanelFrame
      title={`Inspector · ${element.type}`}
      isCompact={isCompact}
      onClose={onClose}
      headerActions={
        <>
          <Button variant="ghost" size="icon" onClick={() => onDuplicate(element.id)} data-testid="duplicate-element-btn" className="h-7 w-7 rounded-none text-ink3 hover:text-[#FFB400] hover:bg-hover">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(element.id)} data-testid="delete-element-btn" className="h-7 w-7 rounded-none text-ink3 hover:text-[#FF2A00] hover:bg-hover">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      }
    >
      <button
        type="button"
        onClick={() => onReplay?.()}
        data-testid="replay-animation-btn"
        className="w-full h-9 flex items-center justify-center gap-2 bg-[#FF6B00] text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#ff7d1f] transition-colors"
        title="播放动画 · 文字播放打字机效果，图形播放绘制/生成过程"
      >
        <RotateCcw className="h-3.5 w-3.5" /> 播放动画 · Play
      </button>
      <TransformControls type={element.type} attrs={attrs} setAttr={setAttr} />
      {element.type === "text" && <TextControls attrs={attrs} setAttr={setAttr} />}
      {element.type === "image" && (
        <ImageControls attrs={attrs} setAttr={setAttr} onReplaceImage={(file) => onReplaceImage?.(element.id, file)} />
      )}
      {(element.type === "rect" || element.type === "polygon") && (
        <CornerRadiusControls attrs={attrs} setAttr={setAttr} type={element.type} />
      )}
      <StyleControls attrs={attrs} setAttr={setAttr} effects={effects} setEffect={setEffect} />
      <AnimationControls animation={element.animation} setAnim={setAnim} />
      {exportSection}
    </PanelFrame>
  );
}
